import { BitkitList } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Card } from '@chakra-ui/react/card';
import { Text } from '@chakra-ui/react/text';
import camelCase from 'lodash/camelCase';
import { ReactNode, useContext } from 'react';

import BuildLogSummary, { WorkflowSummary } from '../../types/BuildLogSummary';
import StepListItem from './StepListItem';
import StructuredBuildLogContext from './StructuredBuildLogContext';

export const connectorLineStyle = {
  position: 'relative',
  _after: {
    content: '""',
    display: 'block',
    position: 'absolute',
    bottom: '-9px',
    left: '24',
    width: '1px',
    height: '8',
    backgroundColor: 'border/regular',
  },
  _last: {
    _after: { display: 'none' },
  },
};

type WithGroupContainerProps = {
  children: ReactNode[];
  container?: string;
  services?: Record<'image', string>[];
};

const WithGroupContainer = ({ children, container, services }: WithGroupContainerProps) => {
  const serviceImages = services?.map((service) => service.image).join(', ');
  const serviceCount = services?.length || 0;
  const serviceText = serviceCount === 1 ? 'service' : 'services';

  return (
    <Card.Root as="li" elevation={false} paddingSize="sm" css={connectorLineStyle}>
      <Card.Body>
        {!container && !services && (
          <Text as="h5" textStyle="heading/h6" marginBlockEnd="4" color="text/primary" fontWeight="600">
            With group: No container or service is configured.
          </Text>
        )}
        {!!container && !!services && (
          <Text as="h5" textStyle="heading/h6" marginBlockEnd="4" color="text/primary" fontWeight="600">
            In {container} with {serviceImages} {serviceText}
          </Text>
        )}
        {!container && !!services && (
          <Text as="h5" textStyle="heading/h6" marginBlockEnd="4" color="text/primary" fontWeight="600">
            With {serviceImages} {serviceText}
          </Text>
        )}
        {!!container && !services && (
          <Text as="h5" textStyle="heading/h6" marginBlockEnd="4" color="text/primary" fontWeight="600">
            In {container}
          </Text>
        )}
        <BitkitList variant="unstyled">{children}</BitkitList>
      </Card.Body>
    </Card.Root>
  );
};

type StepBundleContainerProps = {
  children: ReactNode[];
  id: string;
  title?: string;
};

const StepBundleContainer = ({ children, id, title }: StepBundleContainerProps) => {
  return (
    <Card.Root as="li" elevation={false} paddingSize="sm" css={connectorLineStyle}>
      <Card.Body>
        <Text as="h5" textStyle="heading/h6" marginBlockEnd="4" color="text/primary" fontWeight="600">
          {title || id}
        </Text>
        <BitkitList variant="unstyled">{children}</BitkitList>
      </Card.Body>
    </Card.Root>
  );
};

const Workflow = ({
  appSlug,
  buildSlug,
  canChangeSettings,
  trackingId,
  triggeredWorkflow,
  viewedAsPublicPage,
  workflow,
}: Omit<StepListProps, 'buildLogSummary'> & { workflow: WorkflowSummary }) => {
  const { execution } = useContext(StructuredBuildLogContext) || {};

  let actualWithGroupId = '';
  let actualWithGroupItems: ReactNode[] = [];

  let actualStepBundleId = '';
  let actualStepBundleItems: ReactNode[] = [];

  const items: ReactNode[] = [];

  workflow.steps.forEach((step, index) => {
    const nextStep = workflow.steps[index + 1];
    const item = (
      <StepListItem
        key={step.uuid}
        appSlug={appSlug}
        buildSlug={buildSlug}
        canChangeSettings={canChangeSettings}
        step={step}
        trackingId={trackingId}
        triggeredWorkflow={triggeredWorkflow}
        viewedAsPublicPage={viewedAsPublicPage}
      />
    );
    if (step.withGroupUuid) {
      actualWithGroupId = step.withGroupUuid;

      if (step.withGroupUuid === actualWithGroupId) {
        actualWithGroupItems.push(item);
      }

      if (nextStep?.withGroupUuid !== actualWithGroupId) {
        const withGroupData = execution?.withGroups?.[camelCase(actualWithGroupId)];
        items.push(
          <WithGroupContainer container={withGroupData?.container?.image || ''} services={withGroupData?.services}>
            {actualWithGroupItems}
          </WithGroupContainer>,
        );
        actualWithGroupItems = [];
      }
    } else if (step.stepBundleUuid) {
      actualStepBundleId = step.stepBundleUuid;

      if (step.stepBundleUuid === actualStepBundleId) {
        actualStepBundleItems.push(item);
      }

      if (nextStep?.stepBundleUuid !== actualStepBundleId) {
        items.push(
          <StepBundleContainer
            id={execution?.stepBundles?.[camelCase(actualStepBundleId)]?.id || ''}
            title={execution?.stepBundles?.[camelCase(actualStepBundleId)]?.title || ''}
          >
            {actualStepBundleItems}
          </StepBundleContainer>,
        );
        actualStepBundleItems = [];
      }
    } else {
      items.push(item);
    }
  });
  return (
    <Box>
      <Text as="h5" textStyle="heading/h6" marginBlockEnd="4" color="text/primary" fontWeight="600">
        {workflow.workflowId}
      </Text>
      {items.length > 0 ? (
        <BitkitList variant="unstyled">{items}</BitkitList>
      ) : (
        <Text color="text/secondary" textStyle="body/md/regular">
          This Workflow contains no Steps.
        </Text>
      )}
    </Box>
  );
};

export type StepListProps = {
  appSlug: string;
  buildSlug: string;
  triggeredWorkflow: string;
  canChangeSettings: boolean;
  viewedAsPublicPage: boolean;
  buildLogSummary?: BuildLogSummary;
  trackingId?: string;
};

export const StepList = ({
  appSlug,
  buildLogSummary,
  buildSlug,
  canChangeSettings,
  trackingId,
  triggeredWorkflow,
  viewedAsPublicPage,
}: StepListProps) => {
  if (!buildLogSummary?.execution?.workflows?.length) {
    return null;
  }

  return (
    <BitkitList variant="unstyled">
      {buildLogSummary.execution.workflows.map((workflow) => (
        <Workflow
          key={workflow.uuid}
          appSlug={appSlug}
          buildSlug={buildSlug}
          canChangeSettings={canChangeSettings}
          trackingId={trackingId}
          triggeredWorkflow={triggeredWorkflow}
          viewedAsPublicPage={viewedAsPublicPage}
          workflow={workflow}
        />
      ))}
    </BitkitList>
  );
};
